import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { CohortMultiSelect } from "@/components/CohortMultiSelect";
import { StatusBadge } from "@/components/StatusBadge";
import { Search, RefreshCw, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { listRegistrations, updateFees } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";

const PAGE_SIZE = 25;

// Finance's whole job: find an application, clear its tuition/hostel fee.
// Deliberately list-only — no drill-in to documents/policies/personal detail
// beyond what's needed to identify the right application.
export default function AdminFinance() {
  const nav = useNavigate();
  const { session } = useAdminAuth();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [cohortIds, setCohortIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (session && session.role !== "finance" && session.role !== "superadmin") {
      nav("/admin", { replace: true });
    }
  }, [session, nav]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    const params = {
      page,
      page_size: PAGE_SIZE,
    };
    if (debouncedQ) params.q = debouncedQ;
    if (cohortIds.length) params.cohort_ids = cohortIds.join(",");
    const data = await listRegistrations(params);
    setRows(data.items || []);
    setTotal(data.total || 0);
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortIds, page, debouncedQ]);

  const applications = useMemo(() => rows.map((r) => ({
    id: r.application_number,
    applicationNumber: r.application_number,
    rollNumber: r.roll_number || "",
    name: r.candidate?.name || "—",
    mobile: r.mobile || "",
    status: r.status || "draft",
    tuitionFeeCleared: !!r.tuition_fee_cleared,
    hostelFeeCleared: !!r.hostel_fee_cleared,
    messChargesCleared: !!r.mess_charges_cleared,
    cautionDepositCleared: !!r.caution_deposit_cleared,
  })), [rows]);

  useEffect(() => { setPage(1); }, [q, cohortIds]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paged = applications;

  const toggleFee = async (appNo, field, current) => {
    const key = `${appNo}-${field}`;
    setSavingKey(key);
    // Optimistic update — flip it locally right away, reconcile after the PATCH resolves.
    setRows((prev) => prev.map((r) => (
      r.application_number === appNo ? { ...r, [field]: !current } : r
    )));
    const res = await updateFees(appNo, { [field]: !current });
    setSavingKey(null);
    if (!res) {
      // Revert on failure.
      setRows((prev) => prev.map((r) => (
        r.application_number === appNo ? { ...r, [field]: current } : r
      )));
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F7F7F7]">
      <DashboardSidebar variant="admin" />
      <main className="flex-1 p-6 lg:p-10 max-w-6xl w-full">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="tiny-label">Admin Console</div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#111] tracking-tight mt-1">Fee Clearance</h1>
            <p className="text-[#6B7280] mt-1">Search for an application and clear its tuition / hostel fee.</p>
          </div>
          <button onClick={() => load()} className="mu-btn-secondary text-sm" data-testid="btn-refresh-finance">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </button>
        </div>

        <div className="mu-card mt-8" data-testid="finance-table">
          <div className="p-4 md:p-5 border-b border-[#E5E7EB] flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="input-search-finance"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, application no., roll no. or mobile"
                className="mu-input pl-9"
              />
            </div>
            <CohortMultiSelect selected={cohortIds} onChange={setCohortIds} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F7F7] text-[#6B7280] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Application No.</th>
                  <th className="text-left px-6 py-3 font-semibold">Candidate</th>
                  <th className="text-left px-6 py-3 font-semibold">Roll No.</th>
                  <th className="text-left px-6 py-3 font-semibold">Status</th>
                  <th className="text-center px-6 py-3 font-semibold">Tuition Fee Cleared</th>
                  <th className="text-center px-6 py-3 font-semibold">Hostel Fee Cleared</th>
                  <th className="text-center px-6 py-3 font-semibold">Mess Charges Cleared</th>
                  <th className="text-center px-6 py-3 font-semibold">Caution Deposit Cleared</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center text-[#9CA3AF]">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading applications…
                    </td>
                  </tr>
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-14 text-center text-[#9CA3AF]">
                      No applications match your search.
                    </td>
                  </tr>
                ) : (
                  paged.map((a) => {
                    const verified = a.status === "verified";
                    return (
                    <tr key={a.id} className="hover:bg-[#F7F7F7]/60 transition" data-testid={`finance-row-${a.id}`}>
                      <td className="px-6 py-3 text-[#111] font-medium">{a.applicationNumber}</td>
                      <td className="px-6 py-3">
                        <div className="text-[#111] font-medium">{a.name}</div>
                        <div className="text-xs text-[#6B7280]">{a.mobile}</div>
                      </td>
                      <td className="px-6 py-3 text-[#6B7280]">{a.rollNumber || "—"}</td>
                      <td className="px-6 py-3"><StatusBadge status={a.status} /></td>
                      <td className="px-6 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={a.tuitionFeeCleared}
                          disabled={(verified && a.tuitionFeeCleared) || savingKey === `${a.id}-tuition_fee_cleared`}
                          onChange={() => toggleFee(a.id, "tuition_fee_cleared", a.tuitionFeeCleared)}
                          className="h-4 w-4 accent-[#B60F2A] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          title={verified && a.tuitionFeeCleared ? "Locked — already cleared before verification" : undefined}
                          data-testid={`checkbox-tuition-${a.id}`}
                        />
                      </td>
                      <td className="px-6 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={a.hostelFeeCleared}
                          disabled={(verified && a.hostelFeeCleared) || savingKey === `${a.id}-hostel_fee_cleared`}
                          onChange={() => toggleFee(a.id, "hostel_fee_cleared", a.hostelFeeCleared)}
                          className="h-4 w-4 accent-[#B60F2A] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          title={verified && a.hostelFeeCleared ? "Locked — already cleared before verification" : undefined}
                          data-testid={`checkbox-hostel-${a.id}`}
                        />
                      </td>
                      <td className="px-6 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={a.messChargesCleared}
                          disabled={(verified && a.messChargesCleared) || savingKey === `${a.id}-mess_charges_cleared`}
                          onChange={() => toggleFee(a.id, "mess_charges_cleared", a.messChargesCleared)}
                          className="h-4 w-4 accent-[#B60F2A] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          title={verified && a.messChargesCleared ? "Locked — already cleared before verification" : undefined}
                          data-testid={`checkbox-mess-${a.id}`}
                        />
                      </td>
                      <td className="px-6 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={a.cautionDepositCleared}
                          disabled={(verified && a.cautionDepositCleared) || savingKey === `${a.id}-caution_deposit_cleared`}
                          onChange={() => toggleFee(a.id, "caution_deposit_cleared", a.cautionDepositCleared)}
                          className="h-4 w-4 accent-[#B60F2A] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          title={verified && a.cautionDepositCleared ? "Locked — already cleared before verification" : undefined}
                          data-testid={`checkbox-caution-${a.id}`}
                        />
                      </td>
                    </tr>
                  );})
                )}
              </tbody>
            </table>
          </div>

          {!loading && total > 0 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-[#E5E7EB]">
              <span className="text-xs text-[#6B7280]">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="mu-btn-secondary text-xs px-2 py-1 disabled:opacity-40"
                  data-testid="btn-finance-prev-page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="text-xs px-1">Page {page} / {pageCount}</span>
                <button
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                  className="mu-btn-secondary text-xs px-2 py-1 disabled:opacity-40"
                  data-testid="btn-finance-next-page"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
